<div class="flex flex-col gap-6">
  <div class="rounded-lg bg-neutral-2 p-4 text-sm text-neutral-11">
    <div class="flex items-start gap-3">
      <div class="flex">
        <mat-icon class="text-primary-9" fontIcon="info"></mat-icon>
      </div>
      <div class="flex flex-col gap-2">
        <span class="font-semibold text-neutral-12"
          >Le DPE est une obligation légale : informez vos futurs acheteurs</span
        >
        <p>
          L'affichage du Diagnostic de Performance Énergétique (DPE) est obligatoire pour vendre votre bien.
          Ne pas le mentionner peut entraîner des sanctions. En plus de respecter la réglementation, ces
          informations aident les acheteurs à mieux se projeter et à prendre leur décision en toute confiance.
          <br />
          Les <strong>DPE vierges</strong> étaient valides jusqu’au 31 décembre 2024.
        </p>
      </div>
    </div>
    @if (propertyLocation()) {
      <div class="mt-3 flex items-center gap-2 text-primary">
        <mat-icon fontIcon="place" class="text-xl"></mat-icon>
        <span class="font-medium">{{ propertyLocation()?.address?.label }}</span>
      </div>
    }
  </div>

  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-2">
      <label class="font-semibold text-neutral-12" for="dpe-state-select"
        >État du Diagnostic de Performance Énergétique (DPE)</label
      >
      <mat-form-field appearance="outline" class="w-full">
        <mat-select
          id="dpe-state-select"
          [formField]="formTree.state"
          (selectionChange)="onDpeStateChange($event.value)"
        >
          @for (state of dpeStates; track state) {
            <mat-option [value]="state">
              {{ state | etxeEnumDisplay: 'dpeState' }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>

    @if (isDpeDone()) {
      <etxe-property-dpe-autocomplete
        [ademeNumber]="model().ademeNumber"
        (ademeNumberChange)="onDpeNumberChange($event)"
        (dpeFound)="onDpeFound($event)"
      ></etxe-property-dpe-autocomplete>

      @if (!hasAdemeData()) {
        <div class="rounded-lg border border-amber-a5 bg-amber-a2 p-4 text-sm text-amber-12">
          <div class="flex items-start gap-3">
            <mat-icon class="text-amber-9" fontIcon="info"></mat-icon>
            <div class="flex flex-col gap-1">
              <span class="font-semibold text-amber-12">Importation du DPE requise</span>
              <p class="text-amber-11">
                Pour valider un DPE réalisé, vous devez renseigner votre numéro ADEME à 13 caractères
                ci-dessus et cliquer sur « Importer » afin de charger les résultats officiels.
              </p>
            </div>
          </div>
        </div>
      } @else {
        <div class="rounded-lg border border-jade-a5 bg-jade-a2 p-4 text-sm text-jade-12">
          <div class="flex items-start gap-3">
            <mat-icon class="text-jade-9" fontIcon="verified"></mat-icon>
            <div class="flex flex-col gap-1">
              <span class="font-semibold text-jade-12">Données issues du registre officiel ADEME</span>
              <p class="text-jade-11">
                Les étiquettes et consommations ont été synchronisées avec succès. Conformément à la
                réglementation, ces données certifiées ne peuvent pas être modifiées manuellement.
              </p>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-6">
          <div class="flex items-center justify-between">
            <h3 class="text-xl font-bold text-neutral-12">Résultats du DPE</h3>
            <span
              class="inline-flex items-center gap-1 rounded-full border border-jade-a5 bg-jade-a2 px-2.5 py-0.5 text-xs font-semibold text-jade-11"
            >
              <mat-icon class="!h-3.5 !w-3.5 !text-xs leading-none">lock</mat-icon>
              Données certifiées (non modifiables)
            </span>
          </div>

          <!-- Realization Date -->
          <div class="flex flex-col gap-1">
            <div class="flex items-center justify-between">
              <label class="font-semibold text-neutral-12" for="realization-date-input"
                >Date de réalisation</label
              >
              <span class="inline-flex items-center gap-1 text-xs text-neutral-10">
                <mat-icon class="!h-3.5 !w-3.5 !text-xs leading-none">lock</mat-icon>
                Certifié ADEME
              </span>
            </div>
            <mat-form-field appearance="outline" class="w-full">
              <input
                id="realization-date-input"
                matInput
                [matDatepicker]="realizationPicker"
                [ngModel]="model().realizationDate"
                readonly
              />
              <mat-datepicker-toggle
                matIconSuffix
                [for]="realizationPicker"
                [disabled]="true"
              ></mat-datepicker-toggle>
              <mat-datepicker #realizationPicker></mat-datepicker>
            </mat-form-field>
          </div>

          <!-- Energy Consumption -->
          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <label class="font-semibold text-neutral-12" for="energy-consumption-input"
                >Consommation en énergie primaire</label
              >
              <span class="inline-flex items-center gap-1 text-xs text-neutral-10">
                <mat-icon class="!h-3.5 !w-3.5 !text-xs leading-none">lock</mat-icon>
                Certifié ADEME
              </span>
            </div>
            <mat-form-field appearance="outline" class="w-full">
              <input
                id="energy-consumption-input"
                matInput
                type="number"
                [ngModel]="model().energyConsumption"
                readonly
                placeholder="0"
              />
              <span matSuffix class="mr-2">kWhEP/m²/an</span>
            </mat-form-field>
            <etxe-energy-class-selector
              type="energy"
              [readonly]="true"
              [value]="model().energyClass ?? undefined"
            ></etxe-energy-class-selector>
          </div>

          <!-- GHG Emissions -->
          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <label class="font-semibold text-neutral-12" for="ghg-emission-input"
                >Émissions de gaz à effet de serre</label
              >
              <span class="inline-flex items-center gap-1 text-xs text-neutral-10">
                <mat-icon class="!h-3.5 !w-3.5 !text-xs leading-none">lock</mat-icon>
                Certifié ADEME
              </span>
            </div>
            <mat-form-field appearance="outline" class="w-full">
              <input
                id="ghg-emission-input"
                matInput
                type="number"
                [ngModel]="model().greenhouseGasEmission"
                readonly
                placeholder="0"
              />
              <span matSuffix class="mr-2">kgCO₂/m²/an</span>
            </mat-form-field>
            <etxe-energy-class-selector
              type="ghg"
              [readonly]="true"
              [value]="model().climateClass ?? undefined"
            ></etxe-energy-class-selector>
          </div>

          <!-- Final Energy -->
          <div class="flex flex-col gap-1">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <label class="font-semibold text-neutral-12" for="final-energy-input"
                  >Consommation en énergie finale</label
                >
                <mat-icon
                  class="text-sm text-neutral-8"
                  fontIcon="info"
                  fontSet="material-icons-outlined"
                  matTooltip="Information énergie finale"
                ></mat-icon>
              </div>
              <span class="inline-flex items-center gap-1 text-xs text-neutral-10">
                <mat-icon class="!h-3.5 !w-3.5 !text-xs leading-none">lock</mat-icon>
                Certifié ADEME
              </span>
            </div>
            <mat-form-field appearance="outline" class="w-full">
              <input
                id="final-energy-input"
                matInput
                type="number"
                [ngModel]="model().finalEnergyConsumption"
                readonly
                placeholder="0"
              />
              <span matSuffix class="mr-2">kWh/m²/an</span>
            </mat-form-field>
          </div>

          @if (model().technical; as tech) {
            <!-- Données techniques et isolation ADEME -->
            <div class="flex flex-col gap-5 rounded-2xl border border-neutral-3 bg-neutral-1 p-5 shadow-xs">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2.5">
                  <mat-icon class="text-primary-10" fontIcon="verified"></mat-icon>
                  <div class="flex flex-col">
                    <span class="text-base font-semibold text-neutral-12"
                      >Données techniques et isolation (ADEME)</span
                    >
                    <span class="text-xs text-neutral-10"
                      >Extraites du registre officiel et pré-remplies dans votre dossier</span
                    >
                  </div>
                </div>
                <span
                  class="inline-flex items-center gap-1 rounded-full border border-jade-a5 bg-jade-a2 px-2.5 py-0.5 text-xs font-semibold text-jade-11"
                >
                  <mat-icon class="!h-3.5 !w-3.5 !text-xs leading-none">auto_awesome</mat-icon>
                  Synchronisé
                </span>
              </div>

              <!-- Isolation Ratings Grid -->
              <div class="flex flex-col gap-2.5">
                <span class="text-xs font-semibold tracking-wider text-neutral-10 uppercase"
                  >Qualité de l'isolation</span
                >
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                  <!-- Murs -->
                  <div class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
                    <span class="text-xs font-medium text-neutral-10">Murs</span>
                    <div class="flex items-center gap-1.5">
                      <span
                        [class]="
                          'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ' +
                          getQualityBadgeClass(tech.wallInsulation)
                        "
                      >
                        <mat-icon class="!h-3 !w-3 !text-xs leading-none">{{
                          getQualityIcon(tech.wallInsulation)
                        }}</mat-icon>
                        {{ tech.wallInsulation || 'Non précisé' }}
                      </span>
                    </div>
                  </div>

                  <!-- Menuiseries / Vitrage -->
                  <div class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
                    <span class="text-xs font-medium text-neutral-10">Menuiseries et vitrage</span>
                    <div class="flex items-center gap-1.5">
                      <span
                        [class]="
                          'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ' +
                          getQualityBadgeClass(tech.glazingQuality)
                        "
                      >
                        <mat-icon class="!h-3 !w-3 !text-xs leading-none">{{
                          getQualityIcon(tech.glazingQuality)
                        }}</mat-icon>
                        {{ tech.glazingQuality || 'Non précisé' }}
                      </span>
                    </div>
                  </div>

                  <!-- Toiture / Combles -->
                  <div class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
                    <span class="text-xs font-medium text-neutral-10">Toiture et combles</span>
                    <div class="flex items-center gap-1.5">
                      <span
                        [class]="
                          'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ' +
                          getQualityBadgeClass(tech.roofInsulation)
                        "
                      >
                        <mat-icon class="!h-3 !w-3 !text-xs leading-none">{{
                          getQualityIcon(tech.roofInsulation)
                        }}</mat-icon>
                        {{ tech.roofInsulation || 'Non précisé' }}
                      </span>
                    </div>
                  </div>

                  <!-- Plancher bas -->
                  <div class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
                    <span class="text-xs font-medium text-neutral-10">Plancher bas</span>
                    <div class="flex items-center gap-1.5">
                      <span
                        [class]="
                          'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ' +
                          getQualityBadgeClass(tech.floorInsulation)
                        "
                      >
                        <mat-icon class="!h-3 !w-3 !text-xs leading-none">{{
                          getQualityIcon(tech.floorInsulation)
                        }}</mat-icon>
                        {{ tech.floorInsulation || 'Non précisé' }}
                      </span>
                    </div>
                  </div>

                  <!-- Enveloppe globale -->
                  <div
                    class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3 sm:col-span-2 lg:col-span-1"
                  >
                    <span class="text-xs font-medium text-neutral-10">Enveloppe globale</span>
                    <div class="flex items-center gap-1.5">
                      <span
                        [class]="
                          'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ' +
                          getQualityBadgeClass(tech.envelopeQuality)
                        "
                      >
                        <mat-icon class="!h-3 !w-3 !text-xs leading-none">{{
                          getQualityIcon(tech.envelopeQuality)
                        }}</mat-icon>
                        {{ tech.envelopeQuality || 'Non précisé' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Équipements & Chauffage Grid -->
              <div class="flex flex-col gap-2.5 border-t border-neutral-3 pt-3">
                <span class="text-xs font-semibold tracking-wider text-neutral-10 uppercase"
                  >Système de chauffage et équipements</span
                >
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <!-- Chauffage -->
                  <div class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
                    <span class="text-xs font-medium text-neutral-10">Installation de chauffage</span>
                    <span
                      class="text-sm font-semibold text-neutral-12 truncate"
                      [title]="
                        (tech.heatingType ? (tech.heatingType | titlecase) : '') +
                        (tech.heatingEnergy ? ' (' + tech.heatingEnergy + ')' : '')
                      "
                    >
                      {{ tech.heatingType ? (tech.heatingType | titlecase) : '' }}
                      {{
                        tech.heatingEnergy
                          ? '(' + tech.heatingEnergy + ')'
                          : !tech.heatingType
                            ? 'Non précisé'
                            : ''
                      }}
                    </span>
                  </div>

                  <!-- Hauteur sous plafond -->
                  <div class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
                    <span class="text-xs font-medium text-neutral-10">Hauteur sous plafond</span>
                    <span class="text-sm font-semibold text-neutral-12">
                      {{ tech.ceilingHeight ? tech.ceilingHeight + ' m' : 'Non renseignée' }}
                    </span>
                  </div>

                  <!-- Climatisation -->
                  <div class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
                    <span class="text-xs font-medium text-neutral-10">Climatisation</span>
                    <div class="flex items-center gap-1.5">
                      @if (tech.hasAirConditioning) {
                        <span
                          class="inline-flex items-center gap-1 rounded-md border border-jade-a5 bg-jade-a2 px-2 py-0.5 text-xs font-semibold text-jade-11"
                        >
                          <mat-icon class="!h-3 !w-3 !text-xs leading-none">ac_unit</mat-icon>
                          Installée
                        </span>
                      } @else {
                        <span class="text-xs text-neutral-10">Non détectée</span>
                      }
                    </div>
                  </div>

                  <!-- Ventilation -->
                  <div class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
                    <span class="text-xs font-medium text-neutral-10">Ventilation</span>
                    <span
                      class="text-sm font-semibold text-neutral-12 truncate"
                      [title]="tech.ventilationType || 'Non précisée'"
                    >
                      {{ tech.ventilationType || 'Non précisée' }}
                    </span>
                  </div>

                  <!-- Générateur / Système -->
                  @if (tech.heatingGenerator) {
                    <div
                      class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3 sm:col-span-2"
                    >
                      <span class="text-xs font-medium text-neutral-10"
                        >Générateur / système de production</span
                      >
                      <span class="text-sm font-semibold text-neutral-12">{{ tech.heatingGenerator }}</span>
                    </div>
                  }

                  <!-- Émetteur(s) -->
                  @if (tech.heatingEmitter) {
                    <div
                      class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3 sm:col-span-2"
                    >
                      <span class="text-xs font-medium text-neutral-10">Émetteur(s) de chaleur</span>
                      <span class="text-sm font-semibold text-neutral-12">{{ tech.heatingEmitter }}</span>
                    </div>
                  }
                </div>
              </div>
            </div>
          }

          <!-- Costs -->
          <div class="flex flex-col gap-4">
            <div class="flex items-center justify-between">
              <label class="font-semibold text-neutral-12" for="estimated-cost-min-input"
                >Estimation des coûts d'énergie du logement</label
              >
              <span
                class="rounded bg-ruby-a2 px-2 py-0.5 text-xs font-medium text-ruby-11 border border-ruby-a5"
              >
                Obligatoire
              </span>
            </div>
            <p class="text-xs text-neutral-10">
              Renseignez la fourchette annuelle figurant sur la page de garde de votre DPE. Elle ne peut pas
              être laissée à zéro.
            </p>
            <div
              id="estimated-costs-slider"
              class="flex flex-col gap-6 rounded-xl border border-neutral-a3 bg-neutral-1 px-4 py-6 transition-all hover:border-primary-a7"
              [class.border-ruby-a7]="isCostInvalid()"
            >
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-1">
                  <label
                    class="text-xs font-semibold tracking-wider text-neutral-10 uppercase"
                    for="estimated-cost-min-input"
                    >Minimum (€/an)</label
                  >
                  <mat-form-field appearance="outline" class="w-full">
                    <input
                      id="estimated-cost-min-input"
                      matInput
                      type="number"
                      min="1"
                      [ngModel]="model().estimatedCostMin"
                      (ngModelChange)="onCostMinChange($event)"
                      placeholder="Ex : 800"
                    />
                    <span matSuffix class="mr-2">€/an</span>
                  </mat-form-field>
                </div>
                <div class="flex flex-col gap-1">
                  <label
                    class="text-xs font-semibold tracking-wider text-neutral-10 uppercase"
                    for="estimated-cost-max-input"
                    >Maximum (€/an)</label
                  >
                  <mat-form-field appearance="outline" class="w-full">
                    <input
                      id="estimated-cost-max-input"
                      matInput
                      type="number"
                      min="1"
                      [ngModel]="model().estimatedCostMax"
                      (ngModelChange)="onCostMaxChange($event)"
                      placeholder="Ex : 1200"
                    />
                    <span matSuffix class="mr-2">€/an</span>
                  </mat-form-field>
                </div>
              </div>

              <div class="relative pt-2">
                <mat-slider class="w-full" min="0" max="2000" step="50" discrete>
                  <input
                    matSliderStartThumb
                    aria-label="Coût annuel minimum"
                    [(ngModel)]="model().estimatedCostMin"
                    (ngModelChange)="onCostMinChange($event)"
                  />
                  <input
                    matSliderEndThumb
                    aria-label="Coût annuel maximum"
                    [(ngModel)]="model().estimatedCostMax"
                    (ngModelChange)="onCostMaxChange($event)"
                  />
                </mat-slider>
              </div>

              <div class="flex justify-between text-[10px] font-medium text-neutral-a8">
                <span>0 €</span>
                <span>1 000 €</span>
                <span>2 000 €</span>
              </div>
            </div>

            @if (isCostInvalid()) {
              <div class="flex items-center gap-1.5 text-xs text-ruby-11">
                <mat-icon class="!h-4 !w-4 !text-xs leading-none">error</mat-icon>
                <span>L'estimation des coûts d'énergie est obligatoire et doit être supérieure à 0 €.</span>
              </div>
            }
          </div>

          <!-- Indexing Date -->
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Date d'indexation du prix des énergies</mat-label>
            <input
              matInput
              [matDatepicker]="picker"
              [ngModel]="model().priceIndexingDate"
              [max]="maxPriceIndexingDate()"
              readonly
            />
            <mat-datepicker-toggle matIconSuffix [for]="picker" [disabled]="true"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
          </mat-form-field>
        </div>
      }
    }
  </div>
</div>
